import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";

export type PopoverPlacement =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "right"
  | "right-start"
  | "right-end";

export interface PopoverProps {
  /** Whether the popover is open */
  open: boolean;
  /** Callback when popover should close */
  onClose?: () => void;
  /** Anchor element reference */
  anchorEl?: HTMLElement | null;
  /** Anchor position (alternative to anchorEl) */
  anchorPosition?: { top: number; left: number };
  /** Which reference to use: anchorEl or anchorPosition */
  anchorReference?: "anchorEl" | "anchorPosition";
  /** Placement of the popover relative to anchor */
  placement?: PopoverPlacement;
  /** Offset from anchor [x, y] */
  offset?: [number, number];
  /** Content of the popover */
  children: React.ReactNode;
  /** Custom class name */
  className?: string;
  /** Disable portal (render inline) */
  disablePortal?: boolean;
  /** Close on click outside */
  closeOnClickOutside?: boolean;
  /** Close on escape key */
  closeOnEscape?: boolean;
  /** Arrow indicator */
  showArrow?: boolean;
  /** Transition duration in ms */
  transitionDuration?: number;
  /** Keep mounted when closed */
  keepMounted?: boolean;
  /** Paper props for styling the container */
  paperProps?: React.HTMLAttributes<HTMLDivElement>;
  /** Transform origin */
  transformOrigin?: {
    vertical: "top" | "center" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  /** Anchor origin */
  anchorOrigin?: {
    vertical: "top" | "center" | "bottom";
    horizontal: "left" | "center" | "right";
  };
}

/**
 * Arc-style Popover component
 *
 * Features:
 * - Portal-based rendering
 * - Multiple placement options
 * - Auto-positioning to stay in viewport
 * - Click outside to close
 * - Escape key to close
 * - Smooth animations
 */
const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      open,
      onClose,
      anchorEl,
      anchorPosition,
      anchorReference = "anchorEl",
      placement = "bottom-start",
      offset = [0, 8],
      children,
      className = "",
      disablePortal = false,
      closeOnClickOutside = true,
      closeOnEscape = true,
      showArrow = false,
      transitionDuration = 200,
      keepMounted = false,
      paperProps,
      transformOrigin,
      anchorOrigin,
    },
    ref
  ) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [isVisible, setIsVisible] = useState(false);
    const [actualPlacement, setActualPlacement] =
      useState<PopoverPlacement>(placement);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Calculate position based on anchor and placement
    const calculatePosition = useCallback(() => {
      if (anchorReference === "anchorPosition" && anchorPosition) {
        setPosition({
          top: anchorPosition.top + offset[1],
          left: anchorPosition.left + offset[0],
        });
        return;
      }

      if (!anchorEl || !popoverRef.current) return;

      const anchorRect = anchorEl.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = 0;
      let left = 0;
      let finalPlacement = placement;

      // Calculate initial position based on placement
      const getInitialPosition = (p: PopoverPlacement) => {
        let t = 0;
        let l = 0;

        // Handle vertical position
        if (p.startsWith("top")) {
          t = anchorRect.top - popoverRect.height - offset[1];
        } else if (p.startsWith("bottom")) {
          t = anchorRect.bottom + offset[1];
        } else if (p.startsWith("left") || p.startsWith("right")) {
          if (p.includes("-start")) {
            t = anchorRect.top;
          } else if (p.includes("-end")) {
            t = anchorRect.bottom - popoverRect.height;
          } else {
            t = anchorRect.top + anchorRect.height / 2 - popoverRect.height / 2;
          }
        }

        // Handle horizontal position
        if (p.startsWith("left")) {
          l = anchorRect.left - popoverRect.width - offset[0];
        } else if (p.startsWith("right")) {
          l = anchorRect.right + offset[0];
        } else if (p.startsWith("top") || p.startsWith("bottom")) {
          if (p.includes("-start")) {
            l = anchorRect.left;
          } else if (p.includes("-end")) {
            l = anchorRect.right - popoverRect.width;
          } else {
            l = anchorRect.left + anchorRect.width / 2 - popoverRect.width / 2;
          }
        }

        return { t, l };
      };

      // Get initial position
      const initial = getInitialPosition(placement);
      top = initial.t;
      left = initial.l;

      // Check if popover fits in viewport and flip if necessary
      const checkAndFlip = () => {
        // Check vertical overflow
        if (placement.startsWith("bottom") && top + popoverRect.height > viewportHeight) {
          const flipped = placement.replace("bottom", "top") as PopoverPlacement;
          const newPos = getInitialPosition(flipped);
          if (newPos.t >= 0) {
            top = newPos.t;
            finalPlacement = flipped;
          }
        } else if (placement.startsWith("top") && top < 0) {
          const flipped = placement.replace("top", "bottom") as PopoverPlacement;
          const newPos = getInitialPosition(flipped);
          if (newPos.t + popoverRect.height <= viewportHeight) {
            top = newPos.t;
            finalPlacement = flipped;
          }
        }

        // Check horizontal overflow
        if (left + popoverRect.width > viewportWidth) {
          left = viewportWidth - popoverRect.width - 8;
        }
        if (left < 8) {
          left = 8;
        }
      };

      checkAndFlip();

      // Constrain to viewport
      top = Math.max(8, Math.min(top, viewportHeight - popoverRect.height - 8));
      left = Math.max(8, Math.min(left, viewportWidth - popoverRect.width - 8));

      setPosition({ top, left });
      setActualPlacement(finalPlacement);
    }, [anchorEl, anchorPosition, anchorReference, placement, offset]);

    // Handle open/close animation
    useEffect(() => {
      if (open) {
        setIsVisible(true);
        // Calculate position after render
        requestAnimationFrame(() => {
          calculatePosition();
        });
      } else {
        const timer = setTimeout(() => {
          if (!keepMounted) {
            setIsVisible(false);
          }
        }, transitionDuration);
        return () => clearTimeout(timer);
      }
    }, [open, calculatePosition, transitionDuration, keepMounted]);

    // Update position on scroll/resize
    useEffect(() => {
      if (!open) return;

      const handleUpdate = () => calculatePosition();
      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);

      return () => {
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
      };
    }, [open, calculatePosition]);

    // Handle click outside
    useEffect(() => {
      if (!open || !closeOnClickOutside) return;

      const handleClickOutside = (e: MouseEvent) => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(e.target as Node) &&
          anchorEl &&
          !anchorEl.contains(e.target as Node)
        ) {
          onClose?.();
        }
      };

      // Delay to prevent immediate close
      const timer = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timer);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [open, closeOnClickOutside, onClose, anchorEl]);

    // Handle escape key
    useEffect(() => {
      if (!open || !closeOnEscape) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose?.();
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [open, closeOnEscape, onClose]);

    // Don't render if not visible and not keeping mounted
    if (!isVisible && !keepMounted) return null;

    // Get arrow position based on placement
    const getArrowStyles = () => {
      const arrowSize = 8;
      const styles: React.CSSProperties = {
        position: "absolute",
        width: 0,
        height: 0,
        borderStyle: "solid",
      };

      if (actualPlacement.startsWith("bottom")) {
        styles.top = -arrowSize;
        styles.borderWidth = `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`;
        styles.borderColor = "transparent transparent white transparent";
        if (actualPlacement === "bottom-start") styles.left = 16;
        else if (actualPlacement === "bottom-end") styles.right = 16;
        else styles.left = "50%";
      } else if (actualPlacement.startsWith("top")) {
        styles.bottom = -arrowSize;
        styles.borderWidth = `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`;
        styles.borderColor = "white transparent transparent transparent";
        if (actualPlacement === "top-start") styles.left = 16;
        else if (actualPlacement === "top-end") styles.right = 16;
        else styles.left = "50%";
      }

      return styles;
    };

    const content = (
      <div
        ref={(node) => {
          popoverRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={`
          bg-surface-elevated
          border border-arc-border
          rounded-arc-md
          shadow-arc-lg
          ${className}
        `}
        style={{
          position: disablePortal ? "absolute" : "fixed",
          top: position.top,
          left: position.left,
          zIndex: 9999,
          opacity: open ? 1 : 0,
          transform: open ? "scale(1)" : "scale(0.95)",
          transformOrigin: transformOrigin
            ? `${transformOrigin.horizontal} ${transformOrigin.vertical}`
            : "top left",
          transition: `opacity ${transitionDuration}ms ease, transform ${transitionDuration}ms ease`,
          pointerEvents: open ? "auto" : "none",
          ...paperProps?.style,
        }}
        {...paperProps}
      >
        {showArrow && <div style={getArrowStyles()} />}
        {children}
      </div>
    );

    if (disablePortal) {
      return content;
    }

    return typeof window !== "undefined"
      ? createPortal(content, document.body)
      : null;
  }
);

Popover.displayName = "Popover";

export { Popover };
export default Popover;









